/**
 * The purpose of this class, is to be a conversion of a flow object, into a set of nodes and edges that can be displayed diagramatically.
 */

import { Flow } from "../model/flow"
import { IStep, IStepAction } from "../model/flow.types"
import { ConditionalValue } from "../model/flowRaw"
import { layout } from "./elkLayout"
import { getUniqueID } from "./getUniqueID"

export interface INode {
  id: string
  width: number
  height: number
  x: number
  y: number
}

class Node implements INode {
  id: string
  width: number
  height: number
  x: number
  y: number

  constructor() {
    this.id = getUniqueID()

    this.width = 400
    this.height = 300
    this.x = 0
    this.y = 0
  }
}

class StepNode extends Node {
  step: IStep

  constructor(step: IStep) {
    super()
    this.step = step
  }
}

class ActionNode extends Node {
  action: IStepAction

  constructor(action: IStepAction) {
    super()
    this.action = action
  }
}

class ConditionNode extends Node {
  condition: ConditionalValue

  constructor(condition: ConditionalValue) {
    super()
    this.condition = condition
  }
}

export interface IEdge {
  id: string
  source: INode
  target: INode
  isHappy: boolean
}

class Edge implements IEdge {
  id: string
  source: INode
  target: INode

  constructor(source: INode, target: INode) {
    this.id = getUniqueID()
    this.source = source
    this.target = target
  }

  get isHappy() {
    return true
  }
}

class ConditionEdge extends Edge {
  previousConditionMet: boolean

  constructor(source: INode, target: INode, condition: boolean) {
    super(source, target)
    this.previousConditionMet = condition
  }

  get isHappy() {
    return this.previousConditionMet
  }
}

export interface IFlowDiagram {
  flow: Flow
  nodes: INode[]
  edges: IEdge[]
}

export class FlowDiagram implements IFlowDiagram {
  public flow: Flow
  public nodes: INode[]
  public edges: IEdge[]

  constructor(flow: Flow) {
    this.flow = flow
    this.nodes = []
    this.edges = []

    this.addAllStepsFromFlow()
    this.addAllActionsFromFlow()
  }

  public getEdges() {
    return this.edges
  }

  public async getNodes() {
    await layout(this.nodes, this.edges).then(
      (result: { nodes: INode[]; edges: IEdge[] }) => {
        this.nodes = result.nodes
        this.edges = result.edges
      },
    )
    return this.nodes
  }

  public getNode(id: string): INode | undefined {
    return this.nodes.find((o) => o.id === id)
  }

  private getOrAddStepNode(stepID: string): INode {
    let node = this.getAllStepNodes().find((o) => o.step.id === stepID)
    if (node) {
      return node
    }
    const step = this.flow.steps.find((o) => o.id === stepID)
    if (!step) {
      throw Error(
        `Tried accessing step (step id = ${stepID}) which doesn't seem to exist on flow (flow id = ${this.flow.helpTitle})`,
      )
    }
    node = new StepNode(step)
    this.nodes.push(node)
    return node
  }

  // ACTIONS DON'T HAVE UNIQUE ID, SO CANNOT JUST GRAB EXISTING.
  private addActionNode(action: IStepAction): ActionNode {
    const node = new ActionNode(action)
    this.nodes.push(node)
    return node
  }

  // ACTIONS DON'T HAVE UNIQUE ID, SO CANNOT JUST GRAB EXISTING.
  private addConditionNode(condition: ConditionalValue): ConditionNode {
    const node = new ConditionNode(condition)
    this.nodes.push(node)
    return node
  }

  private getAllStepNodes(): StepNode[] {
    return this.nodes.filter((o) => o instanceof StepNode) as StepNode[]
  }

  private connect(source: INode, target: INode, conditionMet?: boolean) {
    if (conditionMet === true || conditionMet === false) {
      this.edges.push(new ConditionEdge(source, target, conditionMet))
    } else {
      this.edges.push(new Edge(source, target))
    }
  }

  private addAllStepsFromFlow() {
    for (const step of this.flow.steps) {
      this.getOrAddStepNode(step.id)
    }
  }

  private addAllActionsFromFlow() {
    for (const step of this.flow.steps) {
      const stepNode = this.getOrAddStepNode(step.id)
      for (const action of step.actions ?? []) {
        const actionNode = this.addActionNode(action)
        this.connect(stepNode, actionNode)

        if (action.nextId) {
          const nextStepNode = this.getOrAddStepNode(action.nextId)
          this.connect(actionNode, nextStepNode)
        } else if (action.nextIf) {
          let previousNode: ActionNode | ConditionNode = actionNode
          for (const clause of action.nextIf) {
            if ("if" in clause && clause.if) {
              const conditionNode = this.addConditionNode(clause.if)
              this.connect(
                previousNode,
                conditionNode,
                previousNode instanceof ActionNode,
              )
              previousNode = conditionNode
              const nextStepNode = this.getOrAddStepNode(clause.step)
              this.connect(previousNode, nextStepNode, true)
            } else {
              const nextStepNode = this.getOrAddStepNode(clause.step)
              this.connect(previousNode, nextStepNode, false)
            }
          }
        } else if (action.saveTo) {
          // add a save node..
        }
      }
    }
  }
}
